/**
 * InstructionBanner — context-aware "what to do next" overlay.
 *
 * Sits in the strip between the top profile cards and the bet zones, anchored
 * centered. State classes (BankerSetPotState, FadingState, PlayerBettingState,
 * HiLoState, RollState) call ctx.showInstruction() in onEnter + onUpdate to
 * keep the message in sync as game data changes (banker switches, shooter
 * advances, etc.).
 *
 * Layout: optional title + horizontal divider + description text. Pure copy,
 * no chrome — matches the wallet/accumulator tutorial labels: solid black,
 * square corners, no border. The earlier icon/grid sidebet legend was pulled
 * because players misread the icon row as a tap target; the description text
 * now lists the sidebet options inline.
 *
 * Suppression is multi-source. Reasons:
 *   - 'betting-activity': BETTING [P] pill is up; the pill is the higher-info
 *     payload for this strip and wins the slot.
 *   - 'dice-pickup': the local player has dice in hand or is mid-throw
 *     (anything except DiceThrowState.READY). Once you grab and throw, the
 *     banner stays hidden through flight + landing — only re-appears when
 *     the dice are cleared back to READY (typically on the next state).
 * Banner is hidden whenever ANY reason is active; the last payload is
 * remembered so it's restored on un-suppress.
 */

import gsap from 'gsap';

export interface InstructionBannerOptions {
  container: HTMLElement;
}

export interface InstructionPayload {
  /** Optional title rendered above a divider line. */
  title?: string;
  /** The line(s) of guidance text. Required. */
  text: string;
}

/**
 * Reveal-animation strategy. Easy to swap by flipping `animIn` below — both
 * paths produce the same final visual; they just choose what to animate.
 *   - 'fade'    : whole banner fades opacity 0 → 1 (children appear together).
 *   - 'stagger' : title fades in → divider draws L→R → body fades + slides up.
 */
type InstructionAnimIn = 'fade' | 'stagger';

export class InstructionBanner {
  private root: HTMLElement;
  private titleEl: HTMLElement;
  private dividerEl: HTMLElement;
  private textEl: HTMLElement;
  private isShown = false;
  private suppressionReasons = new Set<string>();
  private currentPayload: InstructionPayload | null = null;
  private subtitleVisible = true;
  /** Whether the next reveal should play the stagger animation. State
   * machine passes `animated: false` on late-join / app-resume so the
   * banner snaps to its final state without re-running the intro. */
  private currentAnimated = true;

  /**
   * Reveal-animation strategy. 'fade' = single opacity fade-in for the
   * whole banner (default — clean, components appear together). 'stagger'
   * = title fade → divider draw → body slide-up (more dramatic, available
   * if we want to bring it back later). Toggle here, the rest of the
   * banner code is animation-agnostic.
   */
  private animIn: InstructionAnimIn = 'fade';

  constructor(options: InstructionBannerOptions) {
    InstructionBanner.injectStyles();

    this.root = document.createElement('div');
    this.root.className = 'instruction-banner';
    this.root.style.display = 'none';

    this.titleEl = document.createElement('div');
    this.titleEl.className = 'instruction-banner__title';
    this.root.appendChild(this.titleEl);

    this.dividerEl = document.createElement('div');
    this.dividerEl.className = 'instruction-banner__divider';
    this.root.appendChild(this.dividerEl);

    this.textEl = document.createElement('div');
    this.textEl.className = 'instruction-banner__text';
    this.root.appendChild(this.textEl);

    options.container.appendChild(this.root);
  }

  /**
   * Show or update the banner with the given payload.
   *
   * @param animated  When true (default), play the stagger reveal: title
   *   fade in → divider draws left-to-right → text fades up. State machines
   *   pass `false` for the same reasons they pass animated:false to onEnter
   *   (late-joiners, state:full restoration after visibility resume) so the
   *   banner snaps to its final state without replaying the intro.
   */
  show(payload: InstructionPayload, animated: boolean = true): void {
    this.currentPayload = payload;
    this.currentAnimated = animated;
    if (this.suppressionReasons.size > 0) return;
    this.applyPayload(payload);
    this.reveal(animated);
  }

  /** Hide the banner. Idempotent. Resets the subtitle to visible so the next
   * state that calls show() gets a full banner without inheriting the
   * previous state's collapsed-subtitle pose. State classes that want a
   * collapsed subtitle re-set it explicitly. */
  hide(): void {
    this.currentPayload = null;
    this.subtitleVisible = true;
    this.collapseHide();
  }

  /**
   * Toggle the subtitle (divider + body text) without affecting the title or
   * the banner's overall shown/hidden state. State classes call this when
   * the player has demonstrably read the instruction (e.g. they've engaged
   * with the relevant zones), so the title stays as a header but the
   * sub-copy stops competing with the action.
   *
   * Idempotent. Animates the subtitle's height + opacity so it collapses
   * cleanly into the title row instead of leaving an empty gap.
   */
  setSubtitleVisible(visible: boolean): void {
    if (this.subtitleVisible === visible) return;
    this.subtitleVisible = visible;
    // Re-applying the payload reads currentPayload + subtitleVisible so the
    // text/divider display flags pick up the change. We then animate
    // opacity/height for the in/out transition.
    if (this.currentPayload) this.applyPayload(this.currentPayload);

    // Only animate the body text — the divider stays under the title as a
    // header separator regardless of subtitle state.
    if (visible) {
      gsap.killTweensOf(this.textEl);
      gsap.fromTo(
        this.textEl,
        { opacity: 0 },
        { opacity: 1, duration: 0.2, ease: 'power2.out' },
      );
    } else {
      gsap.killTweensOf(this.textEl);
      gsap.to(this.textEl, {
        opacity: 0,
        duration: 0.18,
        ease: 'power2.in',
        onComplete: () => {
          if (this.subtitleVisible) return;
          this.textEl.style.display = 'none';
        },
      });
    }
  }

  /**
   * Toggle a single suppression reason. Banner stays hidden while ANY reason
   * is active; on the last reason clearing, the most recent payload (if any)
   * is reapplied. Reasons used today: 'betting-activity', 'dice-pickup'.
   */
  setSuppressed(reason: string, value: boolean): void {
    const was = this.suppressionReasons.size > 0;
    if (value) this.suppressionReasons.add(reason);
    else this.suppressionReasons.delete(reason);
    const isNow = this.suppressionReasons.size > 0;
    if (was === isNow) return;
    if (isNow) {
      this.collapseHide();
    } else if (this.currentPayload) {
      this.applyPayload(this.currentPayload);
      // Replay reveal honoring the latest stored animated flag — so a
      // banner that was set with animated:false (late-join restore) and
      // then un-suppressed still snaps in instead of staggering.
      this.reveal(this.currentAnimated);
    }
  }

  destroy(): void {
    gsap.killTweensOf(this.root);
    this.root.remove();
  }

  // ── internals ──────────────────────────────────────────────────────

  private applyPayload(payload: InstructionPayload): void {
    if (payload.title) {
      this.titleEl.textContent = payload.title;
      this.titleEl.style.display = 'block';
      this.dividerEl.style.display = 'block';
    } else {
      this.titleEl.style.display = 'none';
      this.dividerEl.style.display = 'none';
    }
    this.textEl.textContent = payload.text;
    // Only the body text follows subtitleVisible. The divider stays under the
    // title as a visual separator/header even when the body collapses.
    this.textEl.style.display = this.subtitleVisible ? 'block' : 'none';
  }

  private reveal(animated: boolean = true): void {
    if (this.isShown) return;
    this.isShown = true;
    // Kill any in-flight tweens (root + children) BEFORE re-showing so a
    // pending collapseHide doesn't fire its `display: none` callback after
    // us, and a stale stagger doesn't fight the new one.
    gsap.killTweensOf([this.root, this.titleEl, this.dividerEl, this.textEl]);
    this.root.style.display = 'flex';

    // Always set children to their final visual state. The chosen animIn
    // strategy then animates either the root (fade) or individual children
    // (stagger). Keeping the children "settled" on every reveal lets us
    // swap strategies without each path needing to re-baseline transforms.
    gsap.set(this.titleEl, { opacity: 1 });
    gsap.set(this.dividerEl, { scaleX: 1 });
    gsap.set(this.textEl, { opacity: 1, y: 0 });

    if (!animated) {
      // Snap — late-join / state:full restoration. No intro the player
      // just missed in the network.
      gsap.set(this.root, { opacity: 1, y: 0 });
      return;
    }

    if (this.animIn === 'fade') {
      // Whole banner fades in together (~300ms). Cleaner reading; all
      // copy appears at once.
      gsap.fromTo(
        this.root,
        { opacity: 0, y: -6 },
        { opacity: 1, y: 0, duration: 0.3, ease: 'power2.out' },
      );
      return;
    }

    // 'stagger': title fade → divider draw → body slide-up. Reset child
    // states to their pre-stagger values, then animate them sequentially.
    gsap.set(this.root, { opacity: 1, y: 0 });
    gsap.set(this.titleEl, { opacity: 0 });
    gsap.set(this.dividerEl, { scaleX: 0 });
    gsap.set(this.textEl, { opacity: 0, y: -4 });
    gsap.to(this.titleEl, { opacity: 1, duration: 0.2, ease: 'power2.out' });
    gsap.to(this.dividerEl, { scaleX: 1, duration: 0.3, delay: 0.1, ease: 'power2.out' });
    gsap.to(this.textEl, { opacity: 1, y: 0, duration: 0.25, delay: 0.25, ease: 'power2.out' });
  }

  private collapseHide(): void {
    if (!this.isShown) return;
    this.isShown = false;
    gsap.killTweensOf(this.root);
    gsap.to(this.root, {
      opacity: 0,
      duration: 0.18,
      ease: 'power2.in',
      onComplete: () => {
        // Defensive: if reveal() ran after we started this tween, isShown
        // is now true again — don't yank the banner back to display: none.
        if (!this.isShown) {
          this.root.style.display = 'none';
        }
      },
    });
  }

  private static stylesInjected = false;
  private static injectStyles(): void {
    if (InstructionBanner.stylesInjected) return;
    InstructionBanner.stylesInjected = true;
    const style = document.createElement('style');
    style.textContent = `
      /* Ambient — no background block. Text-shadow does the legibility lift
         against the asphalt / brick textures. Sits in the periphery instead
         of competing with the bet zones for attention. */
      .instruction-banner {
        position: fixed;
        top: calc(13rem + env(safe-area-inset-top, 0px));
        left: 50%;
        transform: translateX(-50%);
        max-width: 70vw;
        padding: 0;
        background: transparent;
        color: rgba(255, 255, 255, 0.78);
        font-family: 'Bebas Neue', 'Rajdhani', sans-serif;
        letter-spacing: 0.08em;
        text-align: center;
        pointer-events: none;
        z-index: 44;
        display: flex;
        flex-direction: column;
        align-items: stretch;
        gap: 2px;
        will-change: transform, opacity;
      }

      .instruction-banner__title {
        font-size: 22px;
        color: #FFD700;
        letter-spacing: 0.12em;
        line-height: 1.1;
        text-shadow:
          0 0 5px rgba(0, 0, 0, 0.9),
          0 1px 2px rgba(0, 0, 0, 0.9);
      }

      .instruction-banner__divider {
        width: 48px;
        height: 1px;
        background: #FFD700;
        align-self: center;
        margin: 2px 0 3px;
        /* scaleX 0 → 1 sweep on reveal — origin centered so it draws
           outward from the middle, mirroring the title's centered text. */
        transform-origin: center center;
      }

      .instruction-banner__text {
        font-size: 16px;
        line-height: 1.25;
        color: #FFFFFF;
        white-space: normal;
        /* Keep short instruction strings from leaving an orphan word on a
           solo line ("…PLACE YOUR / BET"). text-wrap: balance distributes
           the breaks so each line carries roughly equal width; falls back
           to default wrapping on older browsers without harm. */
        text-wrap: balance;
        text-shadow:
          0 0 5px rgba(0, 0, 0, 0.95),
          0 1px 2px rgba(0, 0, 0, 0.95);
      }
    `;
    document.head.appendChild(style);
  }
}
